import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  TextField,
  Box,
  Typography,
  Collapse,
  Chip,
  Menu, MenuItem
} from "@mui/material";
import {
  Close as CloseIcon,
  PersonAdd as JoinIcon,
  Share as ShareIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  ExpandMore,
  ExpandLess,
  Public as PublicIcon,
  Lock as PrivateIcon,
} from "@mui/icons-material";
import DeleteIcon from '@mui/icons-material/Delete';
import { ShareChannelModal } from "../ShareChannel";
import { useAudioChannel } from "../../../context/AudioChannelContext";
import { deleteChannel, getChannel } from "../../../Assets/api/api";
import { PasswordModal } from "../PasswordModal/PasswordModal";
import { LoadingButton } from "@mui/lab";
import { useTranslation } from "react-i18next";
import FilterListIcon from '@mui/icons-material/FilterList';

export const DirectoryChannelModal = ({ user }) => {
  const {
    modals,
    closeModal,
    switchModals,
    channelData,
    setChannelData,
    openChannelModal,
    setEditChannelId,
    setJoinChannelName,
    setIsJoining,
    isJoining
  } = useAudioChannel();

  const [searchTerm, setSearchTerm] = useState("");
  const [filteredChannels, setFilteredChannels] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  const [channelForPasswordModal, setChannelForPasswordModal] = useState(null);
  const [channelType, setChannelType] = useState("");
  const [channelId, setChannelId] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [channelName, setChannelName] = useState("");
  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const open = Boolean(anchorEl);
  const { t } = useTranslation();

  const translation = t("channel")
  const commonTranslation = translation.common
  const channelTranslation = translation.body.directoryDialog
  const channelOptionTranslation = translation.body.optionDialog
  const { type, setType } = useAudioChannel();
  // Fetch Channels
  useEffect(() => {
    const fetchChannels = async () => {
      try {
        setLoading(true);
        const response = await getChannel(user.slug, type);

        setChannelData(response.result || []);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching channels:", error);
      }
    };
    modals.directoryChannel && fetchChannels();
  }, [modals.directoryChannel]);

  // Update Filtered Channels when Data Changes
  useEffect(() => {
    setTimeout(() => {
      setFilteredChannels(channelData);
    }, 0);
  }, [channelData]);

  // Search Channels
  const handleSearch = () => {
    if (!searchTerm.trim()) {
      setFilteredChannels(channelData);
      return;
    }

    const lowerCaseSearch = searchTerm.toLowerCase();

    const results = channelData.filter((channel) => {
      // Check if the main channel matches the search term
      const isMainChannelMatch =
        channel.name.toLowerCase().includes(lowerCaseSearch) ||
        channel.createdBy?.toLowerCase().includes(lowerCaseSearch) ||
        channel.privacy?.toLowerCase().includes(lowerCaseSearch);

      // Check if any of the subchannels match the search term
      const isSubChannelMatch = channel.SubChannels?.some((sub) =>
        sub.name.toLowerCase().includes(lowerCaseSearch) ||
        sub.createdBy?.toLowerCase().includes(lowerCaseSearch) ||
        sub.privacy?.toLowerCase().includes(lowerCaseSearch)
      );

      return isMainChannelMatch || isSubChannelMatch;
    });

    setFilteredChannels(results);
  };

  // Toggle SubChannel Row
  const toggleRow = (channelId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [channelId]: !prev[channelId],
    }));
  };

  // Handle Join Channel

  const handleJoinChannel = (channel, type) => {
    if (channel.privacy === "public") {
      setIsJoining(true);
      setJoinChannelName(channel.name);
      setType(channel.preferType)
      closeModal("directoryChannel");
    } else if (channel.privacy === "private") {
      switchModals("directoryChannel", "passwordModal");
      setChannelForPasswordModal(channel);
      setChannelType(type);
    }
  };

  // Handle Share Channel
  const handleShareChannel = (channel) => {
    setChannelId(channel.id);
    setIsPublic(channel.privacy === "public");
    setChannelType(channel.SubChannels?.length > 0 ? "channel" : "subchannel");
    setChannelName(channel.name);
    switchModals("directoryChannel", "shareChannel");
  };

  // Handle Edit Channel
  const handleEditChannel = (channel) => {
    switchModals("directoryChannel", "onDemand");
    setEditChannelId(channel.id);
  };

  // Handle Delete Channel
  const handleDeleteChannel = async (channel) => {

    // Implement delete channel functionality here
    if (channel) {
      const res = await deleteChannel(user.slug, channel.id);
      if (res) {
        setChannelData((prev) =>
          prev.filter((item) => item.id !== channel.id)
        );
      }
    }
  };

  const handleFilterSelect = (filterType) => {
    setSelectedFilter(filterType);
    const result = channelData.filter((channel) => {
      if (channel.preferType === filterType) {
        return channel;
      } else {
        return null;
      }
    })
    setFilteredChannels(result);
    setAnchorEl(null);
  }

  return (
    <>
      <Dialog open={modals.directoryChannel} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 1 }}
          >
            <Typography variant="h5" fontWeight="bold">
              {channelTranslation.header}
            </Typography>
            <IconButton
              onClick={() => { closeModal("directoryChannel"); setType("all"); }}
              size="large"
              sx={{ color: "#494B83" }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 3 }}
          >
            <Box
              display="flex"
              alignItems="center"
              gap={1}
              sx={{ width: "60%" }}
            >
              <TextField
                fullWidth
                size="small"
                placeholder={channelTranslation.searchPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                InputProps={{
                  startAdornment: (
                    <SearchIcon sx={{ mr: 1, color: "#494B83" }} />
                  ),
                }}
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                  },
                }}
              />
              <Button
                variant="contained"
                onClick={handleSearch}
                sx={{
                  backgroundColor: "#494B83",
                  borderRadius: "8px",
                  "&:hover": {
                    backgroundColor: "#363761",
                  },
                }}
              >
                {commonTranslation.search}
              </Button>

              {user.role !== "Student" && type === "all" && (
                <>
                  <IconButton
                    aria-label="filter"
                    aria-controls={open ? 'filter-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                    onClick={(event) => setAnchorEl(event.currentTarget)}
                  >
                    <FilterListIcon />
                  </IconButton>
                  <Menu
                    id="filter-menu"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={() => setAnchorEl(null)}
                    MenuListProps={{
                      'aria-labelledby': 'filter-button',
                    }}
                  >
                    <MenuItem
                      onClick={() => handleFilterSelect('general')}
                      selected={selectedFilter === 'General'}
                      sx={{ fontSize: "14px" }}
                    >
                      {channelOptionTranslation.general}
                    </MenuItem>
                    <MenuItem
                      onClick={() => handleFilterSelect('whiteboard')}
                      selected={selectedFilter === 'Whiteboard'}
                      sx={{ fontSize: "14px" }}
                    >
                      {channelOptionTranslation.whiteboard}
                    </MenuItem>
                  </Menu>
                </>
              )}
            </Box>
            {user.role !== "Student" && (
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#7A381E",
                  borderRadius: "8px",
                  "&:hover": {
                    backgroundColor: "#61291A",
                  },
                }}
                onClick={() => switchModals("directoryChannel", "onDemand")}
              >
                {channelTranslation.createChannel}
              </Button>
            )}
          </Box>

          <TableContainer
            component={Paper}
            sx={{
              mt: 1,
              maxHeight: 500,
              overflowY: "auto",
              borderRadius: "10px",
              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f0f2f5" }}>
                  <TableCell width="5%" sx={{ fontWeight: "bold" }}></TableCell>
                  <TableCell width="30%" sx={{ fontWeight: "bold" }}>
                    {commonTranslation.channelName}
                  </TableCell>
                  <TableCell width="20%" sx={{ fontWeight: "bold" }}>
                    {channelTranslation.ownerName}
                  </TableCell>
                  <TableCell width="15%" sx={{ fontWeight: "bold" }}>
                    {commonTranslation.privacy}
                  </TableCell>
                  <TableCell width="15%" sx={{ fontWeight: "bold" }}>
                    {channelTranslation.join}
                  </TableCell>
                  <TableCell width="15%" sx={{ fontWeight: "bold" }}>
                    {channelTranslation.share}
                  </TableCell>
                  {user.role !== "Student" && (
                    <>
                      <TableCell width="15%" sx={{ fontWeight: "bold" }}>
                        {channelTranslation.edit}
                      </TableCell>
                      <TableCell width="15%" sx={{ fontWeight: "bold" }}>
                        {channelTranslation.delete}
                      </TableCell>
                    </>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredChannels.map((channel) => (
                  <React.Fragment key={channel.id}>
                    <TableRow hover>
                      <TableCell>
                        {channel.SubChannels?.length > 0 && (
                          <IconButton
                            onClick={() => toggleRow(channel.id)}
                            sx={{ color: "#494B83" }}
                          >
                            {expandedRows[channel.id] ? (
                              <ExpandLess />
                            ) : (
                              <ExpandMore />
                            )}
                          </IconButton>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <Typography fontWeight="medium">
                            {channel.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {channel.createdBy}
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={
                            channel.privacy === "public" ? (
                              <PublicIcon fontSize="small" />
                            ) : (
                              <PrivateIcon fontSize="small" />
                            )
                          }
                          label={channel.privacy === "public" ? commonTranslation.public : commonTranslation.private}
                          size="small"
                          sx={{
                            backgroundColor:
                              channel.privacy === "public"
                                ? "#e3f2fd"
                                : "#fce4ec",
                            color:
                              channel.privacy === "public"
                                ? "#1976d2"
                                : "#c2185b",
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="contained"
                          startIcon={<JoinIcon />}
                          size="small"
                          onClick={() => handleJoinChannel(channel, "channel")}
                          sx={{
                            backgroundColor: "#494B83",
                            "&:hover": {
                              backgroundColor: "#363761",
                            },
                          }}
                        >
                          {channelTranslation.join}
                        </Button>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="contained"
                          startIcon={<ShareIcon />}
                          size="small"
                          onClick={() => handleShareChannel(channel)}
                          sx={{
                            backgroundColor: "#494B83",
                            "&:hover": {
                              backgroundColor: "#363761",
                            },
                          }}
                        >
                          {channelTranslation.share}
                        </Button>
                      </TableCell>
                      {user.role !== "Student" && (
                        <>
                          <TableCell>
                            <Button
                              variant="contained"
                              startIcon={<EditIcon />}
                              size="small"
                              onClick={() => handleEditChannel(channel)}
                              sx={{
                                backgroundColor: "#494B83",
                                "&:hover": {
                                  backgroundColor: "#363761",
                                },
                              }}
                            >
                              {channelTranslation.edit}
                            </Button>
                          </TableCell>
                          <TableCell>
                            <IconButton aria-label="delete" size="large"
                              onClick={() => handleDeleteChannel(channel)}>
                              <DeleteIcon />
                            </IconButton>

                          </TableCell>
                        </>
                      )}
                    </TableRow>

                    {
                      channel.SubChannels?.length > 0 && (
                        <TableRow>
                          <TableCell
                            colSpan={8}
                            sx={{ padding: 0, borderBottom: "none" }}
                          >
                            <Collapse
                              in={expandedRows[channel.id]}
                              timeout="auto"
                              unmountOnExit
                            >
                              <Box
                                sx={{ p: 2, pl: 6, backgroundColor: "#f5f7fa" }}
                              >
                                <Typography
                                  variant="subtitle2"
                                  gutterBottom
                                  sx={{ fontWeight: "bold", color: "#494B83" }}
                                >
                                  {channelTranslation.subChannels} ({channel.SubChannels.length})
                                </Typography>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell
                                        width="30%"
                                        sx={{
                                          fontWeight: "bold",
                                          border: "none",
                                        }}
                                      >
                                        {channelTranslation.subChannelName}
                                      </TableCell>
                                      <TableCell
                                        width="30%"
                                        sx={{
                                          fontWeight: "bold",
                                          border: "none",
                                        }}
                                      >
                                        {channelTranslation.ownerName}
                                      </TableCell>
                                      <TableCell
                                        width="20%"
                                        sx={{
                                          fontWeight: "bold",
                                          border: "none",
                                        }}
                                      >
                                        {commonTranslation.privacy}
                                      </TableCell>
                                      <TableCell
                                        width="15%"
                                        sx={{
                                          fontWeight: "bold",
                                          border: "none",
                                        }}
                                        onClick={() =>
                                          handleJoinChannel(
                                            channel.SubChannels,
                                            "subchannel"
                                          )
                                        }
                                      >
                                        {channelTranslation.join}
                                      </TableCell>
                                      <TableCell
                                        width="15%"
                                        sx={{
                                          fontWeight: "bold",
                                          border: "none",
                                        }}
                                      >
                                        {channelTranslation.share}
                                      </TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {channel.SubChannels.map((sub) => (
                                      <TableRow key={sub.id} hover>
                                        <TableCell
                                          sx={{
                                            borderBottom: "1px solid #e0e0e0",
                                          }}
                                        >
                                          <Typography variant="body2">
                                            {sub.name}
                                          </Typography>
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            borderBottom: "1px solid #e0e0e0",
                                          }}
                                        >
                                          <Typography variant="body2">
                                            {sub.createdBy}
                                          </Typography>
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            borderBottom: "1px solid #e0e0e0",
                                          }}
                                        >
                                          <Chip
                                            icon={
                                              sub.privacy === "public" ? (
                                                <PublicIcon fontSize="small" />
                                              ) : (
                                                <PrivateIcon fontSize="small" />
                                              )
                                            }
                                            label={sub.privacy === "public" ? commonTranslation.public : commonTranslation.private}
                                            size="small"
                                            sx={{
                                              backgroundColor:
                                                sub.privacy === "public"
                                                  ? "#e3f2fd"
                                                  : "#fce4ec",
                                              color:
                                                sub.privacy === "public"
                                                  ? "#1976d2"
                                                  : "#c2185b",
                                            }}
                                          />
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            borderBottom: "1px solid #e0e0e0",
                                          }}
                                        >
                                          <IconButton
                                            size="small"
                                            sx={{ color: "#494B83" }}
                                            onClick={() =>
                                              handleJoinChannel(sub, "subchannel")
                                            }
                                          >
                                            <JoinIcon fontSize="small" />
                                          </IconButton>
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            borderBottom: "1px solid #e0e0e0",
                                          }}
                                        >
                                          <IconButton
                                            size="small"
                                            sx={{ color: "#494B83" }}
                                            onClick={() =>
                                              handleShareChannel(sub)
                                              // switchModals(
                                              //   "directoryChannel",
                                              //   "shareChannel"
                                              // )
                                            }
                                          >
                                            <ShareIcon fontSize="small" />
                                          </IconButton>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )
                    }
                  </React.Fragment>
                ))}
                {filteredChannels.length === 0 && (
                  <TableRow>
                    {loading ?
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        <LoadingButton />
                      </TableCell>
                      :
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        <Typography variant="subtitle1">
                          {channelTranslation.noChannels}
                        </Typography>
                      </TableCell>
                    }
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </Dialog >

      <ShareChannelModal channelId={channelId} isPublic={isPublic} type={channelType} channelName={channelName} user={user} t={t} />
      <PasswordModal
        channel={channelForPasswordModal}
        channelType={channelType}
        user={user}
        setIsJoining={setIsJoining}
        t={t}
      />
    </>
  );
};

export default DirectoryChannelModal;
